import Seo from "@/components/Seo";
import pages from "./pages.json";

// Single source of truth for all 47 publish-ready pages
// (parsed from the client's content DOCX — see /app/content_import/parse.py)
export const PAGES = pages;

const ORIGIN = process.env.REACT_APP_BACKEND_URL;

export const pageContent = (route) => pages[route];

export const sectionContent = (route, match) =>
  pages[route]?.sections?.find((s) => s.h.toLowerCase().includes(match.toLowerCase()));

// Product pages: card intro from "What Is …", bullet points from "Product Features"
export const productCopy = (route) => {
  const what = sectionContent(route, "what is");
  const feats = sectionContent(route, "product features");
  return {
    desc: what?.blocks?.[0] || "",
    points: (feats?.blocks || []).map((b) => b.split(":")[0].trim()).filter(Boolean),
  };
};

const SOLUTION_ROUTES = new Set([
  "/electronic-data-capture-software-for-clinical-trials",
  "/electronic-consent-management-system",
  "/electronic-patient-reported-outcomes-software",
  "/interactive-web-response-system",
  "/video-consultation-module-for-clinical-trials",
  "/clinical-trial-participant-tracker-software",
]);

const COLLECTION_ROUTES = new Set(["/blog", "/solutions"]);

const abs = (path) => `${ORIGIN}${path === "/" ? "/" : path}`;

const POSTAL_ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: "Plot No 19, Off No 102, Veer Savarkar Block, East Delhi",
  addressLocality: "New Delhi",
  addressRegion: "Delhi",
  postalCode: "110092",
  addressCountry: "IN",
};

const ORGANIZATION_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "eTrialTrack",
  url: `${ORIGIN}/`,
  logo: `${ORIGIN}/assets/etrialtrack-emblem.png`,
  description:
    "Unified, FDA 21 CFR Part 11 compliant eClinical platform: EDC, eConsent, ePRO, RTSM/IWRS, Telehealth and Participant Tracker for clinical trials.",
  email: "info@etrialtrack.in",
  telephone: "+91-9873514525",
  sameAs: [
    "https://www.linkedin.com/company/etrialtrack/",
    "https://www.facebook.com/eTrialTrack/",
    "https://x.com/eTrialTrack",
    "https://www.instagram.com/etrialtrack/",
    "https://share.google/iW3dz6OR46WgKAOc8",
  ],
  founder: [
    { "@type": "Person", name: "Chetna Bogra", jobTitle: "Co-Founder & Director", sameAs: "https://www.linkedin.com/in/chetna-bogra-b351b06a/" },
    { "@type": "Person", name: "Harendra Kumar", jobTitle: "Founder & Director", sameAs: "https://www.linkedin.com/in/harendra-kumar-a177b112/" },
  ],
  address: POSTAL_ADDRESS,
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "sales",
    email: "info@etrialtrack.in",
    telephone: "+91-9873514525",
    areaServed: "Worldwide",
  },
};

const WEBSITE_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "eTrialTrack",
  url: `${ORIGIN}/`,
  publisher: { "@type": "Organization", name: "eTrialTrack" },
};

const LOCAL_BUSINESS_LD = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${ORIGIN}/#local-business`,
  name: "eTrialTrack",
  image: `${ORIGIN}/assets/etrialtrack-emblem.png`,
  url: `${ORIGIN}/`,
  telephone: "+91-9873514525",
  email: "info@etrialtrack.in",
  priceRange: "₹₹",
  openingHours: "Mo-Fr 09:00-19:00",
  address: POSTAL_ADDRESS,
  geo: { "@type": "GeoCoordinates", latitude: 28.63532025070052, longitude: 77.28521813486236 },
  hasMap: "https://maps.app.goo.gl/xVcHRYSexqBAiA2q7",
  areaServed: [
    { "@type": "City", name: "New Delhi" },
    { "@type": "City", name: "Mumbai" },
    { "@type": "City", name: "Hyderabad" },
    { "@type": "City", name: "Ahmedabad" },
    { "@type": "City", name: "Pune" },
    { "@type": "Country", name: "India" },
  ],
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "09:00",
    closes: "19:00",
  },
  sameAs: [
    "https://www.linkedin.com/company/etrialtrack/",
    "https://www.facebook.com/eTrialTrack/",
    "https://x.com/eTrialTrack",
    "https://www.instagram.com/etrialtrack/",
    "https://share.google/iW3dz6OR46WgKAOc8",
  ],
};

const NAV_ITEMS = [
  { name: "Solutions", path: "/solutions" },
  { name: "About Us", path: "/about-us" },
  { name: "Blog", path: "/blog" },
  { name: "Contact Us", path: "/contact-us" },
];

// One line gives any route its full publish-ready meta: title, description,
// keywords, canonical, OG title/description, the page's OG featured image —
// plus auto JSON-LD (WebPage everywhere, BreadcrumbList off-home, Organization/
// WebSite/SiteNavigationElement on home, SoftwareApplication on product pages,
// CollectionPage on listing pages).
export const PageSeo = ({ route, jsonLd = [] }) => {
  const c = pages[route];
  if (!c) return null;
  const url = abs(c.canonicalPath);
  const auto = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: c.title,
      description: c.description,
      url,
      isPartOf: { "@type": "WebSite", name: "eTrialTrack", url: `${ORIGIN}/` },
      publisher: { "@type": "Organization", name: "eTrialTrack" },
      primaryImageOfPage: { "@type": "ImageObject", url: abs(c.ogImage) },
    },
  ];
  auto.push({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement:
      route === "/"
        ? [{ "@type": "ListItem", position: 1, name: "Home", item: `${ORIGIN}/` }]
        : [
            { "@type": "ListItem", position: 1, name: "Home", item: `${ORIGIN}/` },
            { "@type": "ListItem", position: 2, name: c.ogTitle || c.title, item: url },
          ],
  });
  if (route === "/") {
    auto.push(
      ORGANIZATION_LD,
      WEBSITE_LD,
      LOCAL_BUSINESS_LD,
      ...NAV_ITEMS.map((n) => ({
        "@context": "https://schema.org",
        "@type": "SiteNavigationElement",
        name: n.name,
        url: abs(n.path),
      }))
    );
  }
  if (SOLUTION_ROUTES.has(route)) {
    auto.push({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: c.ogTitle || c.title,
      applicationCategory: "BusinessApplication",
      applicationSubCategory: "Clinical Trial Software",
      operatingSystem: "Web",
      description: c.description,
      url,
      image: abs(c.ogImage),
      publisher: { "@type": "Organization", name: "eTrialTrack" },
    });
  }
  if (COLLECTION_ROUTES.has(route)) {
    auto.push({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: c.title,
      description: c.description,
      url,
    });
  }
  return (
    <Seo
      title={c.title}
      description={c.description}
      keywords={c.keywords}
      canonical={c.canonicalPath}
      ogImage={c.ogImage}
      ogTitle={c.ogTitle}
      ogDescription={c.ogDescription}
      jsonLd={[...auto, ...jsonLd]}
    />
  );
};
